import {CHECKIN_STATUS, FARES_FAMILY, FLIGHT_STATUS, MARKET_FLIGHT, REGION_KEY} from "@app/enums";
import { LegAWSInt, PNRLookupAWSInt } from "@app/interfaces/reservation-aws.interface";
import {
  AncillaryInt,
  iropEligibilityInt
} from "@interfaces/*";
import { Segment } from "./segment.model";
import moment from "moment";
import {egRegions} from "@utils/constants/egRegions";
import { getClosingMinutes } from "@app/utils/functions/general";
import { ANCILLARY_ALLOWED_CARRIERS } from '@utils/constants/generals';

export class FlightSummary {
  arrivalAirport: string;
  arrivalCity: string;
  arrivalDateTime: any;
  boardHours: number;
  boardingGate: string | null;
  boardingTerminal: string;
  boardingTime: string;
  boardMinutes: number;
  bookingCabin: string;
  cartId: string;
  checkInRestrictedByStandbyConstraint: boolean;
  checkinStatus: CHECKIN_STATUS;
  currencyCode: string;
  departureAirport: string;
  departureCity: string;
  departureDateTime: any;
  departureTime: string;
  fareFamily: FARES_FAMILY;
  flightHours: number;
  flightMinutes: number;
  flightNumber: string;
  flightStatus: FLIGHT_STATUS;
  infoToFly: string;
  iropEligibility: iropEligibilityInt;
  isDomestic: boolean;
  itineraryPartType: string;
  legCode: string;
  legEntity: string;
  legRegion: string;
  marketFlight: MARKET_FLIGHT;
  operatedByAm: boolean;
  operatingCarrier: string;
  regionKey: REGION_KEY;
  seatSelectionType: string;
  segments: Segment[];
  segmentCode: string;
  serverCartTotal: number;
  totalBoardTimeInHours: number;
  totalFlightTimeInHours: number;
  totalFlightTimeInMinutes: number;
  unpaidAncillaries: AncillaryInt[];
  departureTerminal:string;
  legPercentage: number;
  pallTotalListed: number;
  currentSegment: Segment;
  status: string;
  codeShare: boolean;
  sdsProvider: string;
  serviceCodeShareEnable: boolean;
  windowLegStatus: string;
  timeToDeparture: string;
  remainingTimeToDeparture: string;
  remainingTimeToCheckin: number;

  constructor(pnr: PNRLookupAWSInt, leg: LegAWSInt, kioskLocation: string) {
    const premierFares = ['J', 'F', 'C', 'D', 'I', 'Z', 'O'];
    this.legCode = leg?.legCode;
    this.legEntity = leg?.legEntity;
    this.legRegion = leg?.legRegion;
    this.cartId = pnr.transactionId;
    this.currencyCode = pnr.currency.currencyCode;
    this.windowLegStatus = leg.windowLegStatus;
    this.remainingTimeToCheckin = leg?.remainingTimeToCheckin;

    this.serverCartTotal = pnr.currency.total;
    this.isDomestic = leg.legEntity == 'DOM' ? true : false;


    const segments = leg?.segments;

    if ( segments ) {
      const currentSegment = this.getCurrentSegment(segments, kioskLocation);

      this.legPercentage = egRegions[this.legRegion];

      this.segmentCode = currentSegment.segmentCode;
      this.remainingTimeToDeparture = currentSegment.remainingTimeToDeparture;

      let departureDate = currentSegment.departureDateTime;

      if (currentSegment.estimatedDepartureTime) {
        departureDate = currentSegment.estimatedDepartureTime;
      }

      if (!this.checkinStatus) this.checkinStatus = currentSegment.checkinStatus;

      this.totalFlightTimeInMinutes = leg.totalFlightDuration;
      if ( this.totalFlightTimeInMinutes == null ) {
        this.totalFlightTimeInMinutes = 0;
      }
      this.totalFlightTimeInHours = Number((leg.totalFlightDuration / 60).toFixed(1));
      this.flightHours = Math.floor(this.totalFlightTimeInHours);
      this.flightMinutes = Number(leg.totalFlightDuration) % 60;
      this.boardingGate = null;
      const firstSegment = segments[0];

      this.boardingTime = this.getBoardingTime(moment(firstSegment.departureDateTime));
      this.departureTime = this.getTime(departureDate);
      this.timeToDeparture = this.getTime(departureDate);
      this.departureDateTime = departureDate;
      this.arrivalDateTime = new Date(leg.segments[0].arrivalDateTime);


      if ( segments && segments.length > 0 ) {
        const firstSegment = segments[0];
        const lastSegment = segments[segments.length - 1];
        this.flightNumber = currentSegment?.operatingCarrier + " " + currentSegment?.operatingFlightCode;
        this.departureAirport = firstSegment.departureAirport;
        this.departureCity = this.getCityName(this.departureAirport);
        this.arrivalAirport = lastSegment.arrivalAirport;
        this.arrivalCity = this.getCityName(this.arrivalAirport);
        this.operatingCarrier = currentSegment.operatingCarrier;
        this.fareFamily = firstSegment.fareFamily;

        this.arrivalDateTime = firstSegment.arrivalDateTime;

        if (!firstSegment.fareFamily) {
          const { bookingClass } = firstSegment;
          if (premierFares.indexOf(bookingClass) > -1) {
            this.fareFamily = FARES_FAMILY.PREMIER;
          }else if(bookingClass?.toLocaleUpperCase() === "W"){
            this.fareFamily = FARES_FAMILY.AMPLUS;
          }else if(bookingClass?.toLocaleUpperCase() === "V"){
            this.fareFamily = FARES_FAMILY.BASIC;
          }else{
            this.fareFamily = FARES_FAMILY.CLASSIC
          }
        }
        if(currentSegment) {
          currentSegment.flightNumber = `${currentSegment.operatingCarrier || ''} ${currentSegment?.operatingFlightCode || ''}`;
          this.currentSegment = currentSegment;
          if(currentSegment.windowSegmentStatus === CHECKIN_STATUS.CKI){
            this.checkinStatus = CHECKIN_STATUS.OPEN;
          } else if (currentSegment.estimatedDepartureTime) {
            const now = moment().local(true);
            const etd = moment(currentSegment.estimatedDepartureTime);
            const closingMinutes = getClosingMinutes(this.isDomestic);
            const closingTime = moment(etd).subtract(closingMinutes, 'minutes');
            this.checkinStatus = now.isBefore(closingTime) ? CHECKIN_STATUS.OPEN : CHECKIN_STATUS.CLOSED;
          } else {
            this.checkinStatus = CHECKIN_STATUS.CLOSED;
          }
          this.departureAirport = currentSegment.departureAirport;
        }
        this.segments = this.constructSegment(segments);
      }

      this.unpaidAncillaries = [];
      if ( pnr?.passengers != null && pnr?.passengers.length > 0 ) {
        for (let traveler of pnr?.passengers) {
          if ( traveler.ancillaries != null && traveler.ancillaries,length > 0 ) {
            for (let ancillary of traveler.ancillaries) {
              if ( ancillary != null && ancillary.currency != null && ancillary.currency.total != null && ancillary.currency.total > 0 ) {
                this.unpaidAncillaries.push(ancillary);
              }
            }
          }
        }
      }
    }
  }

  /**
   * true si el vuelo es operado o marketeado por una aerolínea distinta a
   * Aeroméxico (AM) o Aeroméxico Connect (6D). En ese caso el kiosko NO ofrece
   * ni vende ancillaries (solo documenta lo incluido).
   *
   * Caso límite: un carrier nulo/indeterminado NO está en la lista permitida y
   * por tanto se trata como "otra aerolínea" (bloquea). (CI-23321)
   */
  get isOperatedOrMarketedByOtherAirline(): boolean {
    const operating = this.currentSegment?.operatingCarrier?.toUpperCase();
    const marketing = this.currentSegment?.marketingCarrier?.toUpperCase();
    const isAllowed = (carrier?: string): boolean =>
      !!carrier && ANCILLARY_ALLOWED_CARRIERS.includes(carrier);
    // Se permite solo si AMBOS (operador y comercializador) son AM o 6D.
    return !(isAllowed(operating) && isAllowed(marketing));
  }

  /** Semántica positiva para los puntos de entrada del flujo. (CI-23321) */
  get canOfferAncillaries(): boolean {
    return !this.isOperatedOrMarketedByOtherAirline;
  }

  private getCurrentSegment(segments: any[], kioskLocation: string) {
    let segment = segments.find(s => s.windowSegmentStatus === 'CKI');

    if (!segment) {
      segment = segments.find(s => s.windowSegmentStatus === 'MYB');
    }

    if (!segment) {
      const pendingSegments = segments.filter(s =>
        s.windowSegmentStatus !== 'IN_FLIGHT' &&
        s.windowSegmentStatus !== 'USED'
      );
      if (pendingSegments.length > 0) {
        segment = pendingSegments[0];
      }
    }

    if (!segment) {
      segment = segments[segments.length - 1];
    }

    return segment;
  }

  private constructSegment(segments) {
    let arraySegments = [];

    for(let segment of segments) {
       arraySegments.push(new Segment(segment));
    }

    return arraySegments;
  }

  private getBoardingTime(dateTime) {
    let time = '';
    const data = getClosingMinutes(this.isDomestic);

    time = dateTime.subtract(data, 'minutes').format('HH:mm');

    return time;
  }

  private getTime(dateTime) {
    let time = '';

    if ( dateTime != null ) {
      let timeIndex = dateTime.indexOf('T');
      if ( timeIndex != -1 ) {
        timeIndex++;
      }

      time = dateTime.substring(timeIndex, 5);
    }

    return time;
  }

  private getCityName(airportCode) {
    let cityName = "";

    try {
      cityName = "" ;
    } catch (e) {
    }

    return cityName;
  }


}
